import { apiFetch } from "..";

export interface CanvasFormItem {
  id: number;
  canvasId: number;
  vendorItemId: number;
  quantity: number | null;
  budget: number | null;
  price: number | null; // Snapshotted price at time of canvassing
  isVatPrice: boolean | null; // Snapshotted VAT status at time of canvassing
  /** The source RR line's specifics for this item — read live from the RR, never stored. */
  sourceSpecifics?: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: number | null;
  updatedBy: number | null;
  vendorItem?: {
    id: number;
    vendorId: number;
    itemId: number;
    price: number | null; // Current price (not used - for reference only)
    isVatPrice: boolean | null; // Current VAT status (not used - for reference only)
    priceDate: string | null;
    item: {
      id: number;
      name: string;
      description: string | null;
      type: {
        id: number;
        name: string;
      };
      /** The item's base unit — prices compare per base unit. */
      unit?: { id: number; name: string; kind: string | null; toReference: number | null } | null;
    };
    /** The unit this price is quoted per, and its conversion to the base unit. */
    itemUnit?: {
      factor: number | null;
      primaryForItemId: number | null;
      unit: { id: number; name: string; kind: string | null; toReference: number | null };
    } | null;
    vendor: {
      id: number;
      name: string;
      location?: {
        id: number;
        name: string;
      };
    };
  };
}

export interface CanvasForm {
  id: number;
  resourceRequestId: number;
  number: string | null;
  status: "draft" | "for review" | "pending approval" | "approved" | "completed" | "rejected" | null;
  submittedAt: string | null;
  reviewedBy: number | null;
  approvedBy: number | null;
  reviewedAt: string | null;
  approvedAt: string | null;
  rejectedAt: string | null;
  rejectionReason: string | null;
  s3FilePath: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: number | null;
  updatedBy: number | null;
  resourceRequest?: {
    id: number;
    number: string;
    description: string | null;
    status: string | null;
    /** UTC midnight of the day the RR is needed on site. */
    dateNeeded?: string | null;
    project?: {
      id: number;
      code: string;
      name: string;
    };
  };
  canvasFormItems?: CanvasFormItem[];
  creator?: {
    id: number;
    name: string;
  };
  reviewer?: {
    id: number;
    name: string;
  };
  approver?: {
    id: number;
    name: string;
  };
}

export interface CanvasFormsResponse {
  data: CanvasForm[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface CanvasStats {
  status: string | null;
  count: number;
}

export interface FormAction {
  id: number;
  formType: string;
  formId: number;
  action: string;
  userId: number;
  fromStatus: string | null;
  toStatus: string;
  comment: string | null;
  createdAt: string;
  user: {
    id: number;
    name: string;
    position?: string;
  } | null;
}

export interface CreateCanvasFormData {
  resourceRequestId: number;
  canvasFormItems: {
    vendorId: number;
    itemId: number;
    price: number;
    quantity: number;
  }[];
}

export interface UpdateCanvasFormData {
  resourceRequestId: number;
  canvasFormItems: {
    vendorId: number;
    itemId: number;
    price: number;
    isVatPrice: boolean;
    quantity: number;
  }[];
}

export interface WorkflowActionData {
  comment?: string;
}

export const canvasApi = {
  /**
   * Get all canvas forms with pagination
   */
  getAll: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    projectId?: number;
  }): Promise<CanvasFormsResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.search) queryParams.append("search", params.search);
    if (params?.status) queryParams.append("status", params.status);
    if (params?.projectId) queryParams.append("projectId", params.projectId.toString());

    return apiFetch<CanvasFormsResponse>(`/api/canvas?${queryParams.toString()}`);
  },

  /**
   * Get simple list for dropdowns
   */
  getList: (): Promise<
    {
      id: number;
      number: string;
      status: string | null;
      resourceRequestId: number;
    }[]
  > => {
    return apiFetch<
      {
        id: number;
        number: string;
        status: string | null;
        resourceRequestId: number;
      }[]
    >("/api/canvas/list");
  },

  /**
   * Get statistics by status
   */
  getStats: (): Promise<CanvasStats[]> => {
    return apiFetch<CanvasStats[]>("/api/canvas/stats");
  },

  /**
   * Get single canvas form by ID
   */
  getById: (id: number): Promise<CanvasForm> => {
    return apiFetch<CanvasForm>(`/api/canvas/${id}`);
  },

  /**
   * Get canvas form by number
   */
  getByNumber: (number: string): Promise<CanvasForm> => {
    return apiFetch<CanvasForm>(`/api/canvas/by-number/${number}`);
  },

  /**
   * Get PDF document URL
   */
  getDocument: (id: number): Promise<{ url: string | null }> => {
    return apiFetch<{ url: string | null }>(`/api/canvas/${id}/document`);
  },

  /**
   * Get workflow history
   */
  getHistory: (id: number): Promise<FormAction[]> => {
    return apiFetch<FormAction[]>(`/api/canvas/${id}/history`);
  },

  /**
   * Create new canvas form
   */
  create: (data: CreateCanvasFormData): Promise<CanvasForm> => {
    return apiFetch<CanvasForm>("/api/canvas", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  /**
   * Update canvas form (draft only)
   */
  update: (id: number, data: UpdateCanvasFormData): Promise<CanvasForm> => {
    return apiFetch<CanvasForm>(`/api/canvas/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete canvas form (draft only, never submitted)
   */
  delete: (id: number): Promise<{ message: string }> => {
    return apiFetch<{ message: string }>(`/api/canvas/${id}`, {
      method: "DELETE",
    });
  },

  /**
   * Submit canvas for review
   */
  submit: (id: number): Promise<CanvasForm> => {
    return apiFetch<CanvasForm>(`/api/canvas/${id}/submit`, {
      method: "POST",
      body: JSON.stringify({}),
    });
  },

  /**
   * Review canvas form
   */
  review: (id: number, data: WorkflowActionData): Promise<CanvasForm> => {
    return apiFetch<CanvasForm>(`/api/canvas/${id}/review`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  /**
   * Approve canvas form
   */
  approve: (id: number, data: WorkflowActionData): Promise<CanvasForm> => {
    return apiFetch<CanvasForm>(`/api/canvas/${id}/approve`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  /**
   * Reject canvas form
   */
  reject: (id: number, data: { comment: string }): Promise<CanvasForm> => {
    return apiFetch<CanvasForm>(`/api/canvas/${id}/reject`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  /**
   * Return canvas to draft
   */
  returnToDraft: (id: number, data: { comment: string }): Promise<CanvasForm> => {
    return apiFetch<CanvasForm>(`/api/canvas/${id}/return-to-draft`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  /**
   * Mark canvas as completed
   */
  complete: (id: number): Promise<CanvasForm> => {
    return apiFetch<CanvasForm>(`/api/canvas/${id}/complete`, {
      method: "POST",
      body: JSON.stringify({}),
    });
  },
};
